import { v } from "@gencow/core";

export const RAG_BUILTIN_OPERATION_NAMES = {
  documentsIngestStart: "documents.ingest.start",
  documentsIngestReindex: "documents.ingest.reindex",
  ragIngestJobsGet: "rag_ingest_jobs.get",
  ragIngestJobsList: "rag_ingest_jobs.list",
  ragIngestJobsCancel: "rag_ingest_jobs.cancel",
  ragIngestJobsRetry: "rag_ingest_jobs.retry",
  ragIngestJobsResume: "rag_ingest_jobs.resume",
  ragCorporaGet: "rag_corpora.get",
  ragCorporaSetPolicy: "rag_corpora.setPolicy",
  ragOperationsSummary: "rag_operations.summary",
  ragOperationsMetrics: "rag_operations.metrics",
  ragEvaluationsRun: "rag_evaluations.run",
  ragReindexPlan: "rag_reindex.plan",
} as const;

export const ragBuiltinInputSchemas = {
  documentsIngestStart: v.object({
    storageId: v.string(),
    corpus: v.string(),
    visibility: v.string(),
    ownerUserId: v.optional(v.string()),
    sourceKey: v.optional(v.string()),
    metadata: v.optional(v.any()),
    reindexMode: v.optional(v.string()),
    mode: v.optional(v.string()),
    provider: v.optional(v.string()),
  }),
  documentsIngestReindex: v.object({
    sourceId: v.string(),
    mode: v.optional(v.string()),
    provider: v.optional(v.string()),
  }),
  ragIngestJobsGet: v.object({ id: v.string() }),
  ragIngestJobsList: v.object({
    limit: v.optional(v.number()),
    status: v.optional(v.string()),
    corpus: v.optional(v.string()),
  }),
  ragIngestJobsById: v.object({ id: v.string() }),
  ragCorporaGet: v.object({
    corpus: v.string(),
    visibility: v.string(),
    ownerUserId: v.optional(v.string()),
  }),
  ragCorporaSetPolicy: v.object({
    corpus: v.string(),
    visibility: v.string(),
    ownerUserId: v.optional(v.string()),
    allowExternalProviders: v.boolean(),
    metadata: v.optional(v.any()),
  }),
  ragOperationsSummary: v.object({ corpus: v.optional(v.string()) }),
  ragOperationsMetrics: v.object({
    corpus: v.optional(v.string()),
    operation: v.optional(v.string()),
    limit: v.optional(v.number()),
  }),
  ragEvaluationsRun: v.object({ fixtures: v.array(v.any()) }),
  ragReindexPlan: v.object({
    corpus: v.string(),
    visibility: v.string(),
    mode: v.string(),
    reason: v.string(),
    sourceIds: v.optional(v.array(v.string())),
    confirmFullRebuild: v.optional(v.boolean()),
  }),
} as const;

const ragIngestStartOutput = v.object({
  workflowId: v.string(),
  jobId: v.string(),
  sourceId: v.string(),
  status: v.string(),
});

const ragIngestJobOutput = v.object({
  id: v.string(),
  workflowId: v.string(),
  sourceId: v.string(),
  corpus: v.string(),
  visibility: v.string(),
  ownerUserId: v.nullable(v.string()),
  status: v.string(),
  stage: v.string(),
  providerTrace: v.any(),
  metrics: v.any(),
  error: v.nullable(v.string()),
  startedAt: v.string(),
  completedAt: v.nullable(v.string()),
});

const ragJobActionOutput = v.object({
  ok: v.boolean(),
  workflowId: v.nullable(v.string()),
});

const ragJobScheduleActionOutput = v.object({
  ok: v.boolean(),
  workflowId: v.nullable(v.string()),
  scheduledJobId: v.nullable(v.string()),
  action: v.nullable(v.string()),
});

const ragCorpusPolicyOutput = v.object({
  corpus: v.string(),
  visibility: v.string(),
  ownerUserId: v.nullable(v.string()),
  allowExternalProviders: v.boolean(),
  metadata: v.any(),
  exists: v.boolean(),
});

const ragLatestJobOutput = v.object({
  id: v.string(),
  status: v.string(),
  stage: v.string(),
  updatedAt: v.string(),
});

const ragOperationsSummaryOutput = v.object({
  corpus: v.string(),
  visibility: v.string(),
  ownerUserId: v.nullable(v.string()),
  sourceCount: v.number(),
  sectionCount: v.number(),
  chunkCount: v.number(),
  jobCounts: v.any(),
  latestJob: v.nullable(ragLatestJobOutput),
  recentMetricCounts: v.any(),
  averageLatencyMs: v.nullable(v.number()),
  indexHealth: v.string(),
});

const ragOperationMetricOutput = v.object({
  id: v.string(),
  appId: v.string(),
  corpus: v.string(),
  visibility: v.string(),
  ownerUserId: v.nullable(v.string()),
  operation: v.string(),
  jobId: v.nullable(v.string()),
  workflowId: v.nullable(v.string()),
  sourceId: v.nullable(v.string()),
  metricName: v.string(),
  metricValue: v.number(),
  unit: v.nullable(v.string()),
  metadata: v.any(),
  recordedAt: v.string(),
});

const ragEvaluationClaimStatusOutput = v.object({
  claim: v.string(),
  expected: v.string(),
  actual: v.nullable(v.string()),
});

const ragEvaluationFixtureResultOutput = v.object({
  name: v.string(),
  ok: v.boolean(),
  failures: v.array(v.string()),
  matchedSourceIds: v.array(v.string()),
  citationCount: v.number(),
  claimStatuses: v.array(ragEvaluationClaimStatusOutput),
});

const ragEvaluationRunOutput = v.object({
  ok: v.boolean(),
  total: v.number(),
  passed: v.number(),
  failed: v.number(),
  results: v.array(ragEvaluationFixtureResultOutput),
});

const ragReindexPlanOutput = v.object({
  corpus: v.string(),
  visibility: v.string(),
  ownerUserId: v.nullable(v.string()),
  mode: v.string(),
  reason: v.string(),
  sourceIds: v.array(v.string()),
  sourceCount: v.number(),
  estimatedChunkCount: v.number(),
  requiresConfirmation: v.boolean(),
});

export const ragBuiltinOutputSchemas = {
  documentsIngestStart: ragIngestStartOutput,
  documentsIngestReindex: ragIngestStartOutput,
  ragIngestJobsGet: v.nullable(ragIngestJobOutput),
  ragIngestJobsList: v.array(ragIngestJobOutput),
  ragIngestJobsCancel: ragJobActionOutput,
  ragIngestJobsRetry: ragJobScheduleActionOutput,
  ragIngestJobsResume: ragJobScheduleActionOutput,
  ragCorporaGet: ragCorpusPolicyOutput,
  ragCorporaSetPolicy: ragCorpusPolicyOutput,
  ragOperationsSummary: v.array(ragOperationsSummaryOutput),
  ragOperationsMetrics: v.array(ragOperationMetricOutput),
  ragEvaluationsRun: ragEvaluationRunOutput,
  ragReindexPlan: ragReindexPlanOutput,
} as const;
